import {without} from 'lodash';

import {isShallowEqual} from '../../../helpers/reselectHelpers';
import {AppAction} from '../../appActions';
import {AppPanelActionsEnum} from './appPanelActions';
import {AppPanelState} from './appPanelState';
import {
  bannersReducer,
  errorBannerUidsReducer,
  undoBannerUidsReducer,
  visibleBannerUidsReducer,
} from './banners/bannersReducer';
import {AppContentTypesEnum} from './parameters/appParameters';

export function appPanelReducer(state: AppPanelState, action: AppAction): AppPanelState {
  switch (action.type) {
    case AppPanelActionsEnum.NAVIGATED: {
      const {contentType} = action.payload;

      return {
        ...state,
        contentType,
        defaultContentType:
          computeNextDefaultContentType(state.contentType, contentType) ?? state.defaultContentType,
      };
    }
    case AppPanelActionsEnum.NAVIGATION_BLOCKED: {
      const isNavigationBlocked = state.navigationBlockerIds.length > 0;
      if (!isNavigationBlocked) {
        return state;
      }

      return {
        ...state,
        blockedNavigation: action.payload,
      };
    }

    case AppPanelActionsEnum.NAVIGATION_CANCELED:
      return {
        ...state,
        blockedNavigation: undefined,
      };

    case AppPanelActionsEnum.NAVIGATION_BLOCKER_REGISTERED:
      return {
        ...state,
        navigationBlockerIds: [
          ...without(state.navigationBlockerIds, action.payload.blockerId),
          action.payload.blockerId,
        ],
      };

    case AppPanelActionsEnum.NAVIGATION_BLOCKER_UNREGISTERED: {
      const navigationBlockerIds = without(state.navigationBlockerIds, action.payload.blockerId);
      const isNavigationStillBlocked = navigationBlockerIds.length > 0;

      return {
        ...state,
        navigationBlockerIds,
        blockedNavigation: isNavigationStillBlocked ? state.blockedNavigation : undefined,
      };
    }

    case AppPanelActionsEnum.OPEN_HELP_CHAT:
      return {
        ...state,
        helpChatOpenAttempt: state.helpChatOpenAttempt + 1,
      };

    case AppPanelActionsEnum.OPEN_DEMO_INBOX_LOADING_MODAL:
      return {
        ...state,
        isDemoInboxLoadingModalOpen: true,
      };

    case AppPanelActionsEnum.CLOSE_DEMO_INBOX_LOADING_MODAL:
      return {
        ...state,
        isDemoInboxLoadingModalOpen: false,
      };

    case AppPanelActionsEnum.OPEN_INVITE_TEAMMATE_POPOVER:
      return {
        ...state,
        inviteTeammatePopover: {accessPoint: action.payload.accessPoint},
      };

    case AppPanelActionsEnum.CLOSE_INVITE_TEAMMATE_POPOVER:
      return {
        ...state,
        inviteTeammatePopover: undefined,
      };

    default: {
      const newState: AppPanelState = {
        ...state,
        banners: bannersReducer(state.banners, action),
        visibleBannerUids: visibleBannerUidsReducer(state.visibleBannerUids, action),
        undoBannerUids: undoBannerUidsReducer(state.undoBannerUids, action),
        errorBannerUids: errorBannerUidsReducer(state.errorBannerUids, action),
      };
      return isShallowEqual(state, newState) ? state : newState;
    }
  }
}

const validNextDefaultContentTypes = Object.freeze([
  AppContentTypesEnum.INBOXES,
  AppContentTypesEnum.CALENDARS,
  AppContentTypesEnum.CONTACTS,
  AppContentTypesEnum.ANALYTICS,
  AppContentTypesEnum.SEQUENCES,
  AppContentTypesEnum.AUDIT_TRAIL,
]);

function computeNextDefaultContentType(
  prevContentType: AppContentTypesEnum,
  nextContentType: AppContentTypesEnum,
): AppContentTypesEnum | undefined {
  // If we didn't switch apps, nothing to do.
  if (prevContentType === nextContentType) {
    return undefined;
  }

  // If we switched from an app that we don't want to go back to, nothing to do.
  if (!validNextDefaultContentTypes.includes(prevContentType)) {
    return undefined;
  }

  return prevContentType;
}
